import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAllContexts } from "../../../hooks/useAllContexts";

const SubSystemButton = ({ type }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [localFilters, setLocalFilters] = useState([]);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null); // <-- Ref for the dropdown
  const { t } = useTranslation();

  const [data, setData] = useState([]);

  const { environment, meteo, parking, hydro, mesh, variables, sensorTypes } =
    useAllContexts();

  const { SensorTypesData } = sensorTypes;
  const { environmentalData, availableSensors } = environment;
  const { meteoInfoData, meteoSensors } = meteo;
  const { MeshData } = mesh;
  const { hydroData } = hydro;
  const { getSensorFilters, setSensorFilters } = variables;

  const search = useLocation().search;
  const currTypeURL = new URLSearchParams(search).get("typeId");

  useEffect(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({ top: rect.top, left: rect.left });
    }
    if (type === "subsystem") {
      setData(SensorTypesData);
    } else {
      switch (currTypeURL) {
        case "1":
          setData(availableSensors);
          break;
        case "5":
          setData(meteoSensors);
          break;
        default:
          setData([]);
          break;
      }
    }
  }, [currTypeURL]); // Run only once after mount

  useEffect(() => {
    if (type === "variable") {
      setLocalFilters(getSensorFilters);
    }
  }, [getSensorFilters, showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Container key={type}>
      <Button
        $isDropdownVisible={showDropdown}
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <span>{t(`settings.${type}`)}</span>
      </Button>
      {showDropdown &&
        createPortal(
          <Dropdown
            $top={position.top}
            $left={position.left}
            ref={dropdownRef} // <-- Attach dropdown ref
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Title>{t(`settings.select_${type}`)}</Title>
              {localFilters.length > 0 && (
                <ClearFilters
                  onClick={() => {
                    setLocalFilters([]);
                    setSensorFilters([]);
                  }}
                >
                  Επαναφορά
                </ClearFilters>
              )}
            </div>
            <DropdownList $type={type} $listSize={data?.length < 8}>
              {data?.map((item, id) => {
               
                return type === "subsystem" ? (
                  <ListItem
                    onClick={() => setShowDropdown(false)}
                    key={`${type}_list_item_${id}`}
                    to={`/map?typeId=${item.id}`}
                  >
                    {item.title}
                  </ListItem>
                ) : (
                  <CheckboxItem
                    $isItemIncluded={localFilters.includes(item)}
                    key={`${type}_checkbox_${id}`}
                    onClick={() => {
                      if (localFilters.includes(item)) {
                        setLocalFilters(
                          localFilters.filter(
                            (filterItem) => filterItem !== item,
                          ),
                        );
                      } else {
                        setLocalFilters([...localFilters, item]);
                      }
                    }}
                  >
                    {t(item)}
                  </CheckboxItem>
                );
              })}
              {type === "variable" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <ConfirmationButton
                    onClick={() => {
                      setLocalFilters(getSensorFilters);
                      setShowDropdown(false);
                    }}
                  >
                    Ακύρωση
                  </ConfirmationButton>
                  <ConfirmationButton
                    onClick={() => {
                      setSensorFilters(localFilters);
                      setShowDropdown(false);
                    }}
                    $isConfirmation={true}
                  >
                    Εφαρμογη
                  </ConfirmationButton>
                </div>
              )}
            </DropdownList>
          </Dropdown>,
          document.body,
        )}
    </Container>
  );
};

export default SubSystemButton;

const Container = styled.div`
  position: relative;
`;

const Button = styled.div`
  width: 148px;
  height: 30px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  background-color: ${({ $isDropdownVisible }) =>
    $isDropdownVisible ? "#ddd" : "transparent"};
  font-weight: ${({ $isDropdownVisible }) =>
    $isDropdownVisible ? "600" : "500"};

  border: 1px solid #cccccc4f;
  border-radius: 4px;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: #ddd;
    transition: all 0.3s ease-in-out;
    cursor: pointer;
    font-weight: 600;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top + 40}px;
  padding: 20px 30px;
  background-color: #fff;
  z-index: 600;
  border-radius: 8px;
  background-color: #f5f5f5;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.div`
  color: darkgray;
`;

const ListItem = styled(Link)`
  color: #333;
  padding: 8px 10px;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #3333331f;
    transition: all 0.3s ease-in-out;
    border-radius: 8px;
  }
`;

const CheckboxItem = styled.label`
  color: #333;
  padding: 8px 4px;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding-inline: 10px;
  background-color: ${({ $isItemIncluded }) =>
    $isItemIncluded ? "#3333331f" : "transparent"};
  border-radius: 8px;
  &:hover {
    background-color: #3333334f;
    transition: all 0.3s ease-in-out;
    border-radius: 8px;
    // padding-left: 14px;
  }
`;

const DropdownList = styled.div`
  max-width: 860px;
  max-height: ${({ $type, $listSize }) =>
    $type === "subsystem" ? "300px" : $listSize ? "200px" : "440px"};
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-wrap: wrap;
`;

const ConfirmationButton = styled.button`
  width: 110px;
  height: 30px;
  border-radius: 6px;
  background-color: ${({ $isConfirmation }) =>
    $isConfirmation ? "#0000ff8f" : "#aaaaaa1f"};
  color: ${({ $isConfirmation }) => ($isConfirmation ? "#ffe" : "#333")};
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${({ $isConfirmation }) =>
      $isConfirmation ? "#00f" : "#aaaaaa8f"};
    color: ${({ $isConfirmation }) => ($isConfirmation ? "#fff" : "#000")};
    transition: all 0.3s ease-in-out;
  }
`;

const ClearFilters = styled.div`
  color: #0000008f;
  transition: all 0.3s ease-in-out;
  &:hover {
    color: #000000;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
  }
`;
